import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useDiscoverSchemaBackgroundQuery,
  useDiscoverSchemaBackgroundResultQuery,
  useDiscoverSourceMutation,
} from "src/graphql";
import { useQueryClient } from "react-query";
import { usePersistedState } from "src/hooks/use-persisted-state";
import { useEffect, useState } from "react";

type UseSchemaDiscovery = {
  discovering: boolean;
  runSourceDiscovery: () => Promise<void>;
  discoverError: Error | null;
};

const SCHEMA_RESULT_INTERVAL_MS = 1000;

export const useSchemaDiscovery = (opts: {
  connectionId: string;
}): UseSchemaDiscovery => {
  const connectionId = opts.connectionId;
  const { value: jobId, set: setJobId } = usePersistedState<string | null>(
    `source-schema-discovery-${connectionId}`,
    null,
  );

  const { backgroundSchemaDiscovery: backgroundDiscoveryEnabled } = useFlags();

  // Clearing `jobId` changes the result query's key, dropping its data and
  // error with it — so a background failure would be observable for a single
  // render. Latch it here (as an Error, so callers can read `.message`) until
  // the next run.
  const [backgroundFailure, setBackgroundFailure] = useState<{
    connectionId: string;
    error: Error;
  } | null>(null);

  const foregroundQuery = useDiscoverSourceMutation();

  const queryClient = useQueryClient();

  const backgroundStartQuery = useDiscoverSchemaBackgroundQuery(
    {
      connectionId,
    },
    {
      enabled: false,
      onSuccess(data) {
        setJobId(data.startBackgroundDiscovery.jobId);
      },
    },
  );

  const backgroundResultQuery = useDiscoverSchemaBackgroundResultQuery(
    {
      jobId: jobId ?? "",
    },
    {
      enabled: Boolean(jobId),
      refetchInterval: SCHEMA_RESULT_INTERVAL_MS,
      select: (data) => data.backgroundDiscoveryResult,
    },
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    const { data, error } = backgroundResultQuery;

    if (data?.__typename === "DiscoverBackgroundSuccess") {
      setJobId(null);
      queryClient.invalidateQueries();
    } else if (data?.__typename === "DiscoverBackgroundFailure") {
      setBackgroundFailure({ connectionId, error: new Error(data.error) });
      setJobId(null);
    } else if (error) {
      setBackgroundFailure({ connectionId, error });
      setJobId(null);
    }
  }, [backgroundResultQuery]);

  if (!backgroundDiscoveryEnabled) {
    return {
      discovering: foregroundQuery.isLoading,
      runSourceDiscovery: async () => {
        await foregroundQuery.mutateAsync({ connectionId });
        return;
      },
      discoverError: foregroundQuery.error,
    };
  }

  return {
    discovering: !!jobId,
    runSourceDiscovery: async () => {
      setBackgroundFailure(null);
      await backgroundStartQuery.refetch();
      return;
    },
    discoverError:
      backgroundStartQuery.error ||
      (backgroundFailure?.connectionId === connectionId
        ? backgroundFailure.error
        : null),
  };
};
